import React, { useRef, useState } from "react"
import { useDispatch, useSelector } from "react-redux";
import * as Icons from "Components/Icons";
import { SocialVisibilityIndicatorControlled } from "Components/SocialVisibilityIndicator";
import { Popover, PopoverBody } from "Components/ReactstrapSlim";
import { AccountClient, SocialVisibility } from "Services/API/API";
import { useApiClient } from "Services/API/useApiClient";
import exhaustiveCheck from "Utils/exhaustiveCheck";
import { socialVisibilitySelector } from "Utils/selectors";

interface VisibilityDefinition {
   name: string,
   description: string,
}

const getVisibilityDefinition = (socialVisibility: SocialVisibility): VisibilityDefinition => {
   switch (socialVisibility) {
      case SocialVisibility.Visible:
         return {
            name: "Visible",
            description: "Your activity will be visible to your coworkers",
         };
      case SocialVisibility.Invisible:
         return {
            name: "Invisible",
            description: "Your activity will not be visible to your coworkers",
         };
      default:
         exhaustiveCheck(socialVisibility);
         return { name: "", description: "" };
   }
}

interface SocialVisibilityOptionProps {
   socialVisibility: SocialVisibility,
   selected: boolean,
   onClick: () => void,
}
const SocialVisibilityOption: React.FC<SocialVisibilityOptionProps> = ({ socialVisibility, selected, onClick }) => {
   const socialVisibilityDefinition = getVisibilityDefinition(socialVisibility);
   return (<div className="social-visibility-option" onClick={(e) => { e.stopPropagation(); onClick(); }}>
      <div className="social-visibility-option-inner">
         <SocialVisibilityIndicatorControlled socialVisibility={socialVisibility} className="social-visibility-option-indicator" />
         <div>
            <div className="social-visibility-option-name">{socialVisibilityDefinition.name}</div>
            <div className="social-visibility-option-description">{socialVisibilityDefinition.description}</div>
         </div>
      </div>
      <div className="social-visibility-option-icon">
         {selected && <Icons.Check />}
      </div>
   </div>)
}

export const SocialVisibilityToggle: React.FC = () => {
   const dispatch = useDispatch<typeof LSStore.instance.dispatch>();
   const socialVisibility = useSelector(socialVisibilitySelector);
   const socialVisibilityDefinition = getVisibilityDefinition(socialVisibility);
   const [isPopoverOpen, setIsPopoverOpen] = useState<boolean>(false);
   const toggleRef = useRef<HTMLDivElement>(null);
   const accountClient = useApiClient(AccountClient);

   const setSocialVisibility = (newSocialVisibility: SocialVisibility) => {
      dispatch(LSStore.userSlice.actions.setSocialVisibility(newSocialVisibility));
      accountClient.setSocialVisibility(newSocialVisibility);
      setIsPopoverOpen(false);
   }

   return (<>
      <div className="social-visibility-toggle" ref={toggleRef} onClick={() => setIsPopoverOpen((cv) => !cv)}>
         <div className="social-visibility-toggle-inner">
            <SocialVisibilityIndicatorControlled socialVisibility={socialVisibility} className="social-visibility-toggle-indicator" />
            <div>
               <div>{socialVisibilityDefinition.name}</div>
               <div className="social-visibility-toggle-description">{socialVisibilityDefinition.description}</div>
            </div>
         </div>
         <div className="social-visibility-toggle-icon">
            <Icons.ArrowRight />
         </div>
      </div>
      <Popover 
      fade={false} 
      placement={"left"} 
      isOpen={isPopoverOpen} 
      target={toggleRef} 
      container={toggleRef.current?.parentElement || undefined}
      toggle={() => { setIsPopoverOpen(false); }} 
      className="social-visibility-toggle-popup"
      hideArrow>
         <PopoverBody className="social-visibility-toggle-popup-body">
            {[SocialVisibility.Visible, SocialVisibility.Invisible].map(v => <SocialVisibilityOption key={v} socialVisibility={v} selected={v == socialVisibility} onClick={() => setSocialVisibility(v)} />)}
         </PopoverBody>
      </Popover>
   </>)
}